"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { FormStatus, Input } from "@/components/ui/Form";
import { toast } from "@/lib/notifications/toast";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import {
  fetchUserReservations,
  cancelReservation,
  type UserReservation,
} from "@/lib/api/c-resource-reservation-user";
import { ResourceBooking } from "./ResourceBooking";
import styles from "./CResourceReservation.module.css";

type ViewMode = "list" | "book";

const PAGE_SIZE = 20;

/**
 * Plugin rezerwacji usera (shell c_user_accounts) - REST/React.
 *
 * Akcja hash-routable (z `sharingContentForApi`): `user-reservation`.
 * Parytet z legacy `C_RESOURCE_RESERVATION_CONTROLLER::userReservation()` - lista
 * nadchodzacych rezerwacji + anulowanie (>3h przed startem).
 *
 * **Grid:** TanStack Table v8 (headless) - caly bounded zbior, sort/filtr/paginacja
 * client-side.
 *
 * UWAGA: tworzenie nowej rezerwacji (kalendarz Syncfusion + availability) NIE jest
 * jeszcze zmigrowane - to kolejna iteracja. Plugin obsluguje liste + anulowanie.
 */

function formatDateTime(raw: string): string {
  if (!raw) return "";
  /* Y-m-d H:i:s -> Y-m-d H:i (bez sekund). */
  const [d, t] = raw.split(" ");
  if (!t) return d || raw;
  return `${d} ${t.slice(0, 5)}`;
}

export function CResourceReservationView() {
  const [rows, setRows] = useState<UserReservation[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "start_date", desc: false }]);
  const [cancelling, setCancelling] = useState<number | null>(null);
  const [mode, setMode] = useState<ViewMode>("list");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { reservations, truncated: tr } = await fetchUserReservations();
      setRows(reservations);
      setTruncated(tr);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const doCancel = useCallback(
    async (r: UserReservation) => {
      const ok = await confirmDialog({
        title: "Anulowanie rezerwacji",
        message: `Czy na pewno chcesz anulować rezerwację zasobu "${r.resource}"?`,
        variant: "danger",
        confirmLabel: "Anuluj rezerwację",
        cancelLabel: "Zamknij",
      });
      if (!ok) return;

      setCancelling(r.id);
      try {
        await cancelReservation(r.id);
        toast.success("Rezerwacja anulowana.");
        await load();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Nie udało się anulować rezerwacji.");
      } finally {
        setCancelling(null);
      }
    },
    [load],
  );

  const columns = useMemo(() => {
    const col = createColumnHelper<UserReservation>();
    return [
      col.accessor("resource", {
        id: "resource",
        header: () => "Zasób",
        cell: (ctx) => <span className={styles.resourceName}>{ctx.getValue()}</span>,
      }),
      col.accessor("start_date", {
        id: "start_date",
        header: () => "Początek",
        cell: (ctx) => <span className={styles.dateCell}>{formatDateTime(ctx.getValue())}</span>,
      }),
      col.accessor("end_date", {
        id: "end_date",
        header: () => "Koniec",
        cell: (ctx) => <span className={styles.dateCell}>{formatDateTime(ctx.getValue())}</span>,
      }),
      col.accessor("add_date", {
        id: "add_date",
        header: () => "Zarezerwowano",
        cell: (ctx) => <span className={styles.dateCell}>{formatDateTime(ctx.getValue())}</span>,
      }),
      col.display({
        id: "action",
        header: () => <span className="sr-only">Akcje</span>,
        cell: (ctx) => {
          const r = ctx.row.original;
          if (!r.cancellable) {
            return <span className={styles.cancelMuted}>—</span>;
          }
          return (
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => void doCancel(r)}
              disabled={cancelling === r.id}
              title="Anuluj rezerwację"
            >
              <Icon name="x-circle" className={styles.actionIcon} />
              Anuluj
            </button>
          );
        },
      }),
    ];
  }, [doCancel, cancelling]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className={styles.root}>
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${mode === "list" ? styles.tabActive : ""}`}
          onClick={() => setMode("list")}
        >
          <Icon name="calendar" className={styles.actionIcon} />
          Moje rezerwacje
        </button>
        <button
          type="button"
          className={`${styles.tab} ${mode === "book" ? styles.tabActive : ""}`}
          onClick={() => setMode("book")}
        >
          <Icon name="plus-circle" className={styles.actionIcon} />
          Nowa rezerwacja
        </button>
      </div>

      {mode === "book" ? (
        <ResourceBooking
          onBooked={() => {
            setMode("list");
            void load();
          }}
        />
      ) : (
        renderReservationsList()
      )}
    </div>
  );

  function renderReservationsList() {
    return (
      <>
        <p className={styles.note}>Twoje nadchodzące rezerwacje zasobów.</p>

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj (zasób)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Szukaj rezerwacji"
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">Wczytywanie rezerwacji...</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">Brak nadchodzących rezerwacji.</FormStatus>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={sorted ? styles.sortIconActive : styles.sortIcon}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className={styles.truncatedHint}>
              Wyświetlono pierwsze 500 rezerwacji. Zawęź wyszukiwanie aby znaleźć kolejne.
            </p>
          )}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                Poprzednia
              </button>
              <span className={styles.pageInfo}>
                Strona {pageIndex + 1} z {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Następna
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}
      </>
    );
  }
}

/**
 * Czy plugin obsluguje dana akcje (registry w ExtensionActionView).
 */
export function cResourceReservationHandlesAction(actionId: string): boolean {
  return actionId === "user-reservation";
}
